import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { FORM_TYPES, FORM_TYPE_LABEL, FORM_TYPE_ACCENT, BLOCK_DEFAULT_FORM_TYPE } from "@/lib/formTypes";
import { Search, Users, Clock, Check } from "lucide-react";
import { toast } from "sonner";

// "Aus Bibliothek": schnelle Such-/Auswahlansicht ohne die Trainingseinheit zu verlassen.
// Priorisiert Formen des zum Block passenden Typs sowie mit passenden Schwerpunkten.
export default function FormPickerDialog({
  open, blockKey, blockLabel, focusAreas = [], sessionFocusIds = [], onClose, onPick,
}) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");   // "" = Alle
  const [focusFilter, setFocusFilter] = useState([]);

  const defaultType = BLOCK_DEFAULT_FORM_TYPE[blockKey] || "";

  useEffect(() => {
    if (!open) return;
    setQ("");
    setFocusFilter([]);
    setTypeFilter(defaultType);
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/exercise-forms");
        setForms(data);
      } catch {
        toast.error("Bibliothek konnte nicht geladen werden");
      } finally { setLoading(false); }
    })();
  }, [open, defaultType]);

  const focusSet = useMemo(() => new Set(sessionFocusIds), [sessionFocusIds]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = forms.filter((f) => {
      if (typeFilter && f.form_type !== typeFilter) return false;
      if (focusFilter.length && !focusFilter.every((id) => (f.focus_area_ids || []).includes(id))) return false;
      if (term) {
        const hay = `${f.name} ${(f.focus_names || []).join(" ")} ${f.ablauf || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    // Priorisierung: passender Typ zuerst, dann Formen mit Trainings-Schwerpunkt.
    const score = (f) => {
      let s = 0;
      if (defaultType && f.form_type === defaultType) s += 2;
      if ((f.focus_area_ids || []).some((id) => focusSet.has(id))) s += 1;
      return s;
    };
    return [...list].sort((a, b) => score(b) - score(a));
  }, [forms, q, typeFilter, focusFilter, defaultType, focusSet]);

  const usedFocus = useMemo(() => {
    const ids = new Set();
    for (const f of forms) for (const id of f.focus_area_ids || []) ids.add(id);
    return focusAreas.filter((fa) => ids.has(fa.id));
  }, [forms, focusAreas]);

  const toggleFocus = (id) =>
    setFocusFilter((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-5 pb-3 border-b border-zinc-800">
          <DialogTitle className="font-display text-lg font-bold tracking-tight" data-testid="form-picker-title">
            Aus Bibliothek – {blockLabel}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Passende Formen werden zuerst angezeigt. Filter jederzeit änderbar.
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 pt-3 space-y-3 border-b border-zinc-800">
          <div className="relative">
            <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Suche nach Name, Schwerpunkt…"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 pl-9 focus-visible:ring-brand"
              data-testid="form-picker-search" />
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="form-picker-type-filters">
            <FilterChip active={typeFilter === ""} onClick={() => setTypeFilter("")} label="Alle" testId="form-picker-type-all" />
            {FORM_TYPES.map((t) => (
              <FilterChip key={t.key} active={typeFilter === t.key} onClick={() => setTypeFilter(t.key)}
                label={t.label} testId={`form-picker-type-${t.key}`} />
            ))}
          </div>
          {usedFocus.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="form-picker-focus-filters">
              {usedFocus.map((fa) => (
                <FilterChip key={fa.id} active={focusFilter.includes(fa.id)} onClick={() => toggleFocus(fa.id)}
                  label={fa.name} subtle testId={`form-picker-focus-${fa.id}`} />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 pt-4 grid grid-cols-1 lg:grid-cols-2 gap-2 content-start" data-testid="form-picker-results">
          {loading ? (
            <div className="text-center text-sm text-zinc-500 py-10 lg:col-span-2">Lädt…</div>
          ) : results.length === 0 ? (
            <div className="text-center text-sm text-zinc-500 py-10 lg:col-span-2">
              {forms.length === 0
                ? "Noch keine Formen in der Bibliothek. Lege welche im Tab „Bibliothek“ an."
                : "Keine Formen mit diesen Filtern."}
            </div>
          ) : (
            results.map((f) => (
              <ResultRow key={f.id} form={f} onPick={() => onPick?.(f)} />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FilterChip({ active, onClick, label, subtle, testId }) {
  return (
    <button type="button" onClick={onClick}
      className={
        "min-h-[32px] px-3 rounded-full text-xs font-medium tracking-wide transition border " +
        (active
          ? "bg-brand text-white border-brand"
          : subtle
            ? "bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-100 hover:border-zinc-700"
            : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-brand-lighter/50 hover:text-brand-lighter")
      }
      data-testid={testId}>
      {label}
    </button>
  );
}

function ResultRow({ form, onPick }) {
  const spieler = form.spieler_von || form.spieler_bis
    ? `${form.spieler_von ?? "?"}${form.spieler_bis ? `–${form.spieler_bis}` : ""} Spieler`
    : null;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 flex items-start gap-3"
      data-testid={`form-picker-row-${form.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={"inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-bold border " + (FORM_TYPE_ACCENT[form.form_type] || "bg-zinc-800 text-zinc-300 border-zinc-700")}>
            {FORM_TYPE_LABEL[form.form_type] || form.form_type}
          </span>
          <span className="text-sm font-semibold text-zinc-100 truncate">{form.name}</span>
        </div>
        {(form.focus_names || []).length > 0 && (
          <div className="text-xs text-brand-lighter mb-1 truncate">
            {form.focus_names.join(" · ")}
          </div>
        )}
        <div className="flex items-center gap-3 text-[11px] text-zinc-500">
          {form.dauer_min ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {form.dauer_min} Min.</span> : null}
          {spieler ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {spieler}</span> : null}
        </div>
      </div>
      <button type="button" onClick={onPick}
        className="shrink-0 inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-md bg-brand hover:bg-brand-light text-white text-xs font-semibold transition"
        data-testid={`form-picker-pick-${form.id}`}>
        <Check className="h-3.5 w-3.5" /> Übernehmen
      </button>
    </div>
  );
}
